import type {
  WorkspaceIntroCardProps,
  WorkspaceIntroSection,
  WorkspaceIntroStep,
} from '../../../../shared/ui/common/WorkspaceIntroCard';
import type { ExportGuideContentArgs } from './types';

const section = (title: string, bullets: string[]): WorkspaceIntroSection => ({
  title,
  bullets,
});

const step = (label: string, goal: string, checks?: string[]): WorkspaceIntroStep => ({
  label,
  goal,
  checks,
});

export const buildExportGuideContent = ({
  systemCount,
  selectedCount,
  outputPath,
  loading,
}: ExportGuideContentArgs): WorkspaceIntroCardProps => ({
  eyebrow: 'Workspace orientation',
  title: 'Export review is the final step before handoff.',
  summary:
    'Use Export to review the final system batch after Mapping, Quick Fill, and Validation are complete.',
  hint:
    'Refresh the system list first, review system statuses before making a selection, show only the additional columns needed for review, and confirm the export destination before exporting files. Use F6 and Shift+F6 to move between export sections, and Esc to close the active panel or dialog.',
  status: loading
    ? 'Refreshing systems'
    : selectedCount > 0
      ? `${selectedCount} systems selected`
      : systemCount > 0
        ? 'Ready for review'
        : 'Waiting for systems',
  actionLabel: 'Start tour',
  chips: [
    `${systemCount} systems loaded`,
    selectedCount > 0 ? `${selectedCount} selected` : 'No systems selected',
    outputPath.trim() ? 'Output path set' : 'Choose output path on export',
    'Final handoff review',
  ],
  sections: [
    section('Use this when', [
      'Mapping, cleanup, and validation are complete and the model is ready for final export review.',
      'You need to determine which piping systems are ready for export and where the exported files should be saved.',
    ]),
    section('Not for', [
      'Do not use Export to determine how unresolved warnings or errors should be repaired.',
      'Do not begin the final export until the system list has been reviewed and is ready for downstream use.',
    ]),
    section('Focus during this review', [
      'Review system statuses before making a selection so warnings and errors are not overlooked.',
      'Use Pick in Revit and export when you need to verify the selected elements directly in the Revit model.',
      'Use Manage columns to control the information shown in the table and Settings to configure export behavior.',
      'Confirm the selected systems and export destination before exporting.',
      'Use F6 and Shift+F6 to move between the command bar, systems table, Manage columns panel, and export actions.',
    ]),
    section('Handoff', [
      'Before handoff, confirm that the system batch has been reviewed, the export destination is correct, and no unresolved warnings or errors remain.',
      'If export readiness is still uncertain, return the issue to the appropriate upstream workflow instead of exporting a batch that requires further review.',
    ]),
  ],
  steps: [
    step('1. Refresh the system list', 'Start with the latest system data after any meaningful model or workflow change.', [
      'The list reflects the current model state.',
    ]),
    step('2. Review status before selection', 'Review warnings and errors to determine which systems are ready for export.', [
      'You know which systems still require additional work before export.',
      'When a table row is focused, press Enter or Space to select it. Open the status chip to review validation details.',
      'Use the Model action when you need to locate the system elements in Revit.',
    ]),
    step('3. Show only the context you need', 'Show system properties or columns labeled "Component" when they help explain grouping, warnings, or export scope.', [
      'The table shows enough information for review without unnecessary columns.',
      'Use the search in Manage columns when the parameter list is long.',
    ]),
    step('4. Confirm the batch, settings, and destination', 'Confirm the selected systems, review Settings when export rules need attention, and verify the output folder before starting the export.', [
      'The selected systems match the intended export.',
      'Use Settings to configure export behavior and Manage columns only to control what is shown in the table.',
      'The correct export destination is selected.',
      'Press Esc to close the active panel, dialog, guide, or tour and return to the export workspace.',
    ]),
    step('5. Export only when the review is complete', 'Treat export as the final step for a batch that has already been reviewed and is ready for export.', [
      'No unresolved warnings, errors, or uncertainties remain.',
    ]),
  ],
});
